import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './Auth.scss';
import CommonInput from '../Components/CommonInput';
import CommonButton from '../Components/CommonButton';
import { send_otp, bank_signup } from '../services';
import ButtonLoader from '../Components/ButtonLoader';
import OtpInput from 'react-otp-input';

function BankSignup() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    // Form fields
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [otp, setOtp] = useState('');

    // UI state
    const [step, setStep] = useState('form'); // 'form' | 'otp'
    const [errors, setErrors] = useState({});
    const [strength, setStrength] = useState(0);
    const [resMsg, setResMsg] = useState('');
    const [errResMsg, setErrResMsg] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPass, setShowPass] = useState(false);
    const [showConfirmPass, setShowConfirmPass] = useState(false);

    // OTP timer
    const [showResend, setShowResend] = useState(true);
    const [timer, setTimer] = useState(30);

    useEffect(() => {
        let interval;
        if (step === 'otp' && showResend && timer > 0) {
            interval = window.setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else if (timer === 0) {
            setShowResend(false);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [step, showResend, timer]);

    const validate = () => {
        const newErrors = {};
        const emailRegex = /^(?![.\-])[A-Za-z0-9._%-]+(?<![.\-])@[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*(?:\.[A-Za-z]{2,})+$/;
        const passwordRegex = /^(?=.{8,}$)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()]).*$/;
        const nameRegex = /^(?!\s+$)[A-Za-z\s]+$/;

        if (!firstName) {
            newErrors.firstName = 'First name is required.';
        } else if (!nameRegex.test(firstName)) {
            newErrors.firstName = 'Name should contain only alphabets.';
        }

        if (!lastName) {
            newErrors.lastName = 'Last name is required.';
        } else if (!nameRegex.test(lastName)) {
            newErrors.lastName = 'Last Name should contain only alphabets.';
        }

        if (!email) {
            newErrors.email = 'Email is required.';
        } else if (!emailRegex.test(email)) {
            newErrors.email = 'Enter a valid email.';
        }

        if (!password) {
            newErrors.password = 'Password is required.';
        } else if (!passwordRegex.test(password)) {
            newErrors.password = 'Password must be ≥8 chars and include lowercase, uppercase, number & special char.';
        }

        if (!confirmPassword) {
            newErrors.confirm = 'Please confirm your password.';
        } else if (confirmPassword !== password) {
            newErrors.confirm = 'Passwords do not match.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onChangeHandler = (e) => {
        const { name, value } = e.target;
        const setters = {
            firstName: setFirstName,
            lastName: setLastName,
            email: setEmail,
            password: setPassword,
            confirmPassword: setConfirmPassword,
        };
        if (setters[name]) {
            setters[name](value);
            setErrResMsg('');
        }
        if (name === 'password') {
            setStrength(calculateStrength(value));
        }
        setErrors({});
    };

    const calculateStrength = (password) => {
        let s = 0;
        if (password.length >= 10) s++;
        if (/[a-z]/.test(password)) s++;
        if (/[A-Z]/.test(password)) s++;
        if (/\d/.test(password)) s++;
        if (/[!@#$%^&*()]/.test(password)) s++;
        return Math.min(s, 4);
    };

    // Step 1: Send OTP
    const handleSendOtp = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        if (!validate()) {
            setIsLoading(false);
            return;
        }

        try {
            const payload = {
                emailId: email,
                otp_type: 'EMAIL_VERIFICATION',
            };
            const response = await send_otp(payload);
            setIsLoading(false);

            const successMessage = response?.statusCode?.response_data?.message;
            const errorMessage = response?.errors?.message || response?.error?.message;

            if (successMessage) {
                setResMsg(successMessage);
                setErrResMsg('');
                setStep('otp');
                setShowResend(true);
                setTimer(30);
            } else if (errorMessage) {
                setErrResMsg(errorMessage);
                setResMsg('');
            }
        } catch (error) {
            setIsLoading(false);
            setErrResMsg('Something went wrong. Please try again.');
        }
    };

    // Step 2: Submit signup with OTP
    const handleSignup = async (e) => {
        e.preventDefault();
        if (!otp.trim() || otp.length < 6) {
            setErrResMsg('Please enter a valid 6-digit OTP.');
            return;
        }
        setIsLoading(true);
        setErrResMsg('');

        try {
            const payload = {
                first_name: firstName,
                last_name: lastName,
                emailId: email,
                password: password,
                confirm_password: confirmPassword,
                otp_code: otp,
                token: token,
            };
            const response = await bank_signup(payload);
            setIsLoading(false);

            const successData = response?.statusCode?.response_data;
            const errorMessage = response?.statusCode?.error?.message || response?.errors?.message || response?.error?.message;

            if (successData?.message) {
                setResMsg(successData.message || 'Signup successful! Redirecting to login...');
                setErrResMsg('');
                setTimeout(() => {
                    navigate('/');
                }, 2000);
            } else if (errorMessage) {
                setErrResMsg(errorMessage);
                setResMsg('');
            }
        } catch (error) {
            setIsLoading(false);
            setErrResMsg('Something went wrong. Please try again.');
        }
    };

    // Resend OTP
    const resendOtp = async () => {
        setIsLoading(true);
        setShowResend(false);
        setTimer(30);
        try {
            const payload = {
                emailId: email,
                otp_type: 'EMAIL_VERIFICATION',
            };
            const response = await send_otp(payload);
            setIsLoading(false);

            const successMessage = response?.statusCode?.response_data?.message;
            const errorMessage = response?.errors?.message;

            if (successMessage) {
                setResMsg(successMessage);
                setErrResMsg('');
                setShowResend(true);
            } else if (errorMessage) {
                setErrResMsg(errorMessage);
                setResMsg('');
            }
        } catch (error) {
            setIsLoading(false);
            setErrResMsg('Failed to resend OTP. Please try again.');
        }
    };

    // No token present
    if (!token) {
        return (
            <div className="sign-up-container">
                <div className="sign-up-wrper">
                    <div className="logo_wrper">
                        <img src="https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/auth_logo.svg" alt="Logo" />
                    </div>
                    <div className="card">
                        <h1 className="title">Invalid Link</h1>
                        <p className="auth_txt_color font_400 font_16">
                            Invalid or missing signup token. Please use the link provided in your invitation email.
                        </p>
                        <CommonButton text="Go to Login" onClick={() => navigate('/')} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="sign-up-container">
            <div className="sign-up-wrper">
                <div className="logo_wrper">
                    <img src="https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/auth_logo.svg" alt="Logo" />
                </div>
                <div className="card">
                    {step === 'form' ? (
                        <>
                            <h1 className="title">Bank Signup</h1>
                            <p className="auth_txt_color font_400 font_16">
                                Complete your registration using the invitation link.
                            </p>
                            <form onSubmit={handleSendOtp} noValidate>
                                <div className="form-group">
                                    <div className="wrp_name_fields">
                                        <div>
                                            <label htmlFor="firstname" className="font_500 font_16 light_black">
                                                First Name
                                            </label>
                                            <CommonInput
                                                id="firstname"
                                                name="firstName"
                                                type="text"
                                                value={firstName}
                                                onChange={onChangeHandler}
                                                placeholder="First Name"
                                                maxLength={20}
                                            />
                                            {errors.firstName && <p className="error-message">{errors.firstName}</p>}
                                        </div>
                                        <div>
                                            <label htmlFor="lastname" className="font_500 font_16 light_black">
                                                Last Name
                                            </label>
                                            <CommonInput
                                                id="lastname"
                                                name="lastName"
                                                type="text"
                                                value={lastName}
                                                onChange={onChangeHandler}
                                                placeholder="Last Name"
                                                maxLength={20}
                                            />
                                            {errors.lastName && <p className="error-message">{errors.lastName}</p>}
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="email" className="font_500 font_16 light_black">
                                        Email
                                    </label>
                                    <CommonInput
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={email}
                                        onChange={onChangeHandler}
                                        placeholder="Enter Email"
                                        maxLength={50}
                                    />
                                    {errors.email && <p className="error-message">{errors.email}</p>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="password" className="font_500 font_16 light_black">
                                        Password
                                    </label>
                                    <CommonInput
                                        id="password"
                                        name="password"
                                        type={showPass ? 'text' : 'password'}
                                        value={password}
                                        onChange={onChangeHandler}
                                        placeholder="Type Strong Password"
                                        maxLength={30}
                                    />
                                    {errors.password && <p className="error-message">{errors.password}</p>}
                                    <span className='eye_icon'>
                                        <img
                                            onClick={() => setShowPass(!showPass)}
                                            src={showPass
                                                ? "https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/blind.svg"
                                                : "https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/show.svg"}
                                            height={20}
                                            width={20}
                                            alt='eye'
                                        />
                                    </span>
                                </div>

                                <div className="strength-indicator">
                                    {Array.from({ length: 4 }).map((_, idx) => (
                                        <div
                                            key={idx}
                                            className={`bar ${idx < strength ? 'filled' : 'empty'}`}
                                        />
                                    ))}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="confirm" className="font_500 font_16 light_black">
                                        Confirm Password
                                    </label>
                                    <CommonInput
                                        id="confirm"
                                        name="confirmPassword"
                                        type={showConfirmPass ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={onChangeHandler}
                                        placeholder="Re-enter Password"
                                        maxLength={30}
                                    />
                                    {errors.confirm && <p className="error-message">{errors.confirm}</p>}
                                    <span className='eye_icon'>
                                        <img
                                            onClick={() => setShowConfirmPass(!showConfirmPass)}
                                            src={showConfirmPass
                                                ? "https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/blind.svg"
                                                : "https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/show.svg"}
                                            height={20}
                                            width={20}
                                            alt='eye'
                                        />
                                    </span>
                                </div>

                                {resMsg && <p className="success-message">{resMsg}</p>}
                                {errResMsg && <p className="error-message">{errResMsg}</p>}
                                <CommonButton type="submit" text={isLoading ? <ButtonLoader /> : "Send OTP"} />
                            </form>

                            <p className="footer-text">
                                Already have an account?{' '}
                                <button type="button" onClick={() => navigate('/')}>
                                    Login
                                </button>
                            </p>
                        </>
                    ) : (
                        <>
                            <h1 className="title">OTP Verification</h1>
                            <p className="auth_txt_color font_400 font_16">
                                Enter below the 6-digit code you received on <strong className='light_black'> {email}</strong>
                            </p>

                            <form onSubmit={handleSignup} noValidate>
                                <div className="form-group">
                                    <div className="otp_inpt">
                                        <OtpInput
                                            value={otp}
                                            onChange={(value) => {
                                                setOtp(value);
                                                setErrResMsg('');
                                            }}
                                            numInputs={6}
                                            renderSeparator={<span>&nbsp;&nbsp;</span>}
                                            renderInput={(props) => <input {...props} />}
                                        />
                                    </div>
                                    <div className="d-flex-end">
                                        {showResend ? (
                                            <p className="resend_otp_forget font_16">
                                                Resend OTP in {timer} seconds
                                            </p>
                                        ) : isLoading ? (
                                            <p className="resend_otp_forget font_16">
                                                Sending OTP
                                            </p>
                                        ) : (
                                            <p className="resend_otp_forget font_16" onClick={resendOtp}>
                                                Resend OTP
                                            </p>
                                        )}
                                    </div>
                                </div>
                                {resMsg && <p className="success-message">{resMsg}</p>}
                                {errResMsg && <p className="error-message">{errResMsg}</p>}
                                <CommonButton type="submit" text={isLoading ? <ButtonLoader /> : "Sign Up"} />
                            </form>

                            <p className="footer-text">
                                <button type="button" onClick={() => { setStep('form'); setOtp(''); setErrResMsg(''); setResMsg(''); }}>
                                    Back to Form
                                </button>
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default BankSignup;
